import { getChartData } from '../Common/HeatmapLineHelpers';
import { GDAModel } from '../../../../models/GDAModel';
import { VDAModel } from '../../../../models/VDAModel';
import { DDAModel } from '../../../../models/DDAModel';
import { getRemainingPages } from '../Common/MultipleRequestsHelpers';
import { wholeNumberParams } from '../Common/Constants';

// structure of an individual heatmap item
class HeatmapItem {
  id: any;
  data: Array<{
    x: any;
    y: any;
    genes: any;
    chartVar: any;
  }>;

  constructor(data: any, yVariable: any, mappingVariables: any[]) {
    this.id = data[mappingVariables[0]];
    this.data = [
      {
        x: data[mappingVariables[1]],
        y: data[yVariable],
        genes: mappingVariables[2] ? data[mappingVariables[2]] : '',
        chartVar: yVariable,
      },
    ];
  }
}

// information needed for constructing the HeatmapChart, depending on the tab selected
const uniqueIdentViewsVariables = {
  GDA: {
    model: GDAModel,
    secondReqIdent: { DISEASES: 'gene_ncbi_id', GENES: 'disease' },
    identList: { DISEASES: ['geneNcbiID'], GENES: ['diseaseUMLSCUI'] },
    mappingIdents: { DISEASES: ['diseaseName', 'geneSymbol'], GENES: ['geneSymbol', 'diseaseName'] },
    comparedValues: { DISEASES: ['Disease', 'Gene'], GENES: ['Gene', 'Disease'] },
  },
  VDA: {
    model: VDAModel,
    secondReqIdent: { DISEASES: 'variant', GENES: ['variant', 'disease'], VARIANTS: 'disease' },
    identList: {
      DISEASES: ['variantStrID'],
      GENES: ['variantStrID', 'diseaseUMLSCUI'],
      VARIANTS: ['diseaseUMLSCUI'],
    },
    mappingIdents: {
      DISEASES: ['diseaseName', 'variantStrID', 'geneSymbol'],
      GENES: ['diseaseName', 'variantStrID', 'geneSymbol'],
      VARIANTS: ['variantStrID', 'diseaseName', 'geneSymbol'],
    },
    comparedValues: {
      DISEASES: ['Disease', 'Variant'],
      GENES: ['Disease', 'Variant'],
      VARIANTS: ['Variant', 'Disease'],
    },
  },
  DDA: {
    model: DDAModel,
    secondReqIdent: { DISEASES: 'disease2' },
    identList: { DISEASES: ['disease2_UMLSCUI'] },
    mappingIdents: { DISEASES: ['disease1_Name', 'disease2_Name'] },
    comparedValues: { DISEASES: ['Disease 1', 'Disease 2'] },
  },
};

// logic for mapping the data from backend into a heatmap chart
function getHeatmapData(data: any, mappingVariables: any, model: any, yVariable: any) {
  const transformedHeatmapData = getChartData(data, yVariable, mappingVariables, model, HeatmapItem);

  const MAX_COLUMNS_PER_ROW = 40;

  const limitedData = transformedHeatmapData?.map(row => {
    if (row.data.length > MAX_COLUMNS_PER_ROW) {
      const sortedData = [...row.data].sort((a, b) => (b.y || 0) - (a.y || 0));
      const limitedRowData = sortedData.slice(0, MAX_COLUMNS_PER_ROW);

      return {
        ...row,
        data: limitedRowData,
      };
    }
    return row;
  });

  return limitedData;
}

function getLegendValues(selectedAttribute: any, data: any[], pagingInfo: any, setLegendValues: any) {
  const isAttributeWholeNumber = wholeNumberParams.includes(selectedAttribute);

  const { currentPageNumber, totalElements, pageSize } = pagingInfo;
  const divergeValue = isAttributeWholeNumber ? 0.15 : 0.4;

  if (currentPageNumber === 0) {
    const maxValue = isAttributeWholeNumber ? data[0][selectedAttribute] : 1;

    setLegendValues((prevLegends: any) => {
      return { ...prevLegends, max: maxValue, divergeAt: divergeValue };
    });
  }

  if (getRemainingPages(currentPageNumber, totalElements, pageSize) === 0) {
    const minValue =
      isAttributeWholeNumber && data.length === 0
        ? 1
        : isAttributeWholeNumber
          ? data[data.length - 1][selectedAttribute]
          : 0;

    setLegendValues((prevLegends: any) => {
      return { ...prevLegends, min: minValue };
    });
  }
}

function getAllArrays(originalArray: any[], arrayToAppend: any[]) {
  const originalIds = originalArray.map((item: any) => item.id);
  const commonItemIds = arrayToAppend.filter((item: any) => originalIds.includes(item.id));
  const differentItemIds = arrayToAppend.filter((item: any) => !originalIds.includes(item.id));

  return [commonItemIds, differentItemIds];
}

function getUniqueXValues(originalData: any[], newData: any[]) {
  const originalXYPairs = new Set(originalData.map(({ x, y }) => `${x}_${y}`));
  const dataToAppend = newData.filter(({ x, y }) => !originalXYPairs.has(`${x}_${y}`));

  return dataToAppend;
}

function unifyArrays(originalArray: any[], arrayToAppend: any[]) {
  if (originalArray.length === 0) {
    return arrayToAppend;
  }

  const [commonItemIds, differentItemIds] = getAllArrays(originalArray, arrayToAppend);

  originalArray.forEach((item: any) => {
    for (let element of commonItemIds) {
      if (item.id === element.id) {
        const newUniqueXValues = getUniqueXValues(item.data, element.data);
        if (newUniqueXValues.length !== 0) {
          item.data.push(...newUniqueXValues);
        }
      }
    }
  });

  const finalArray = originalArray.concat(differentItemIds);
  return finalArray;
}

function getMarginPercent(numberOfParams: number) {
  if (numberOfParams > 80) {
    return 0.05;
  } else if (numberOfParams > 60) {
    return 0.1;
  } else if (numberOfParams > 40) {
    return 0.15;
  } else if (numberOfParams > 20) {
    return 0.2;
  } else if (numberOfParams > 10) {
    return 0.25;
  } else if (numberOfParams > 5) {
    return 0.3;
  } else if (numberOfParams > 3) {
    return 0.35;
  } else {
    return 0.4;
  }
}

function setHeatmapXMargins(width: number, identReqParam: any[]) {
  const numberOfParams = identReqParam[0].split(',').length;
  const viewportWidth = window.innerWidth * 0.7;
  const estimatedColumns = Math.min(numberOfParams * 2, 40);

  let baseMargin;
  if (estimatedColumns > 30) {
    baseMargin = Math.max(viewportWidth * 0.15, 120);
  } else if (estimatedColumns > 20) {
    baseMargin = Math.max(viewportWidth * 0.12, 100);
  } else {
    baseMargin = Math.max(viewportWidth * 0.1, 80);
  }

  return baseMargin;
}

export { uniqueIdentViewsVariables, getHeatmapData, getLegendValues, unifyArrays, setHeatmapXMargins };
